import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { IAddressNotConfirmedUI } from './types'
import { AddressNotConfirmedUI } from './ui'

import { useInput } from '@hooks/useInput'
import { useValue } from '@hooks/useValue'
import { useDebounce } from '@hooks/useDebounce'
import { IAddressNotConfirmedItem, useAddressNotConfirmed } from '@api/addressNotConfirmed'

import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'
import { CardDeliveryContainer, ICardDeliveryContainer } from '@smart/cardDelivery'
import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


/** Контейнер страницы - О неподтвержденных адресах */
export const AddressNotConfirmedContainer = () => {
    const notify = useContext(NotificationContext)

    const addressNotConfirmedState = useAddressNotConfirmed(useShallow((state) => state))
    const modalConfirm = useValue<IModalConfirm>({ isOpen: false })
    const cardDelivery = useValue<ICardDeliveryContainer>({ isOpen: false })
    const txtSearch = useInput<string>('')
    const currentPageNumber = useInput<number>(1)

    useEffect(() => {
        loadPrimaryData(txtSearch.value)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (addressNotConfirmedState.error) notify && notifyOpen(addressNotConfirmedState.error.message, addressNotConfirmedState.error.type, 3000, notify)
    }, [addressNotConfirmedState.error]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => {
            addressNotConfirmedState.clearAddressNotConfirmedState()
            txtSearch.setValue('')
            currentPageNumber.setValue(1)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const loadPrimaryData = (search?: string) => {
        addressNotConfirmedState.actionGetAddressNotConfirmedList(currentPageNumber.value, search || '')
    }

    const debounceSearch = useDebounce(loadPrimaryData, 500)
    const txtSearchOnChange = (value: string) => {
        txtSearch.setValue(value)
        debounceSearch(value)
    }

    const handleOpenDelivery = (contractID: number) => {
        cardDelivery.setValue({
            isOpen: true,
            cardID: contractID,
            handlerButtonCancel() { cardDelivery.setValue({ isOpen: false }) },
            handlerButtonOk() {
                cardDelivery.setValue({ isOpen: false })
                loadPrimaryData(txtSearch.value)
            },
        })
    }

    const handleOpenSendConfirm = (item: IAddressNotConfirmedItem) => {
        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Отправка в канал адресов',
            contentText: `Вы действительно хотите отправить в Telegram канал информацию по заявке № ${item.applicationID}?`,
            handleButtonCancel() { modalConfirm.setValue({ isOpen: false }) },
            handleButtonOk() {
                addressNotConfirmedState.actionSendAddressNotConfirmedToTelegram(item.printID).then((status) => {
                    if (status === 200) notify && notifyOpen('Информация успешно отправлена в Telegram канал адресов', 'success', 1500, notify)
                }).finally(() => {
                    modalConfirm.setValue({ isOpen: false })
                })
            },
        })
    }

    /** Свойства - UI о неподтвержденных адресах */
    const propsUI: IAddressNotConfirmedUI = {
        isLoading: addressNotConfirmedState.addresses.isLoading,
        isSending: addressNotConfirmedState.addresses.isSending,
        sendingPrintID: addressNotConfirmedState.addresses.sendingPrintID,

        txtSearch,
        currentPageNumber,

        items: addressNotConfirmedState.addresses.items,
        totalCount: addressNotConfirmedState.addresses.totalCount,
        pageSize: addressNotConfirmedState.addresses.pageSize,
        weekRange: addressNotConfirmedState.addresses.weekRange,

        loadPrimaryData,
        txtSearchOnChange,
        handleOpenDelivery,
        handleOpenSendConfirm,
    }

    return <>
        <AddressNotConfirmedUI {...propsUI} />
        <ModalConfirm {...modalConfirm.value} />
        <CardDeliveryContainer {...cardDelivery.value} />
    </>
}